/**
 * รูปตัวละครนิ่งสำหรับการ์ดในหน้าเลือกตัวละคร และรูปหมุนได้ในหน้าต่างตัวละคร
 *
 * **ใช้ WebGL context เดียวสำหรับทุกการ์ด** เรนเดอร์ทีละตัวในผืนผ้าใบที่ซ่อนไว้
 * แล้วคัดลอกภาพไปวางบน canvas 2D ของแต่ละการ์ดด้วย `drawImage`
 *
 * ถ้าให้การ์ดละหนึ่ง Engine จะกินห้าคอนเท็กซ์พร้อมกัน ซึ่งเบราว์เซอร์จำกัดไว้
 * (ราว 16 ทั้งแท็บ) และเปลืองหน่วยความจำโดยไม่จำเป็น เพราะรูปพวกนี้นิ่งสนิท
 *
 * ต้องเปิด `preserveDrawingBuffer` ไม่งั้น `drawImage` จะได้ภาพว่าง
 * เพราะเบราว์เซอร์ล้างบัฟเฟอร์ทิ้งหลังวาดจบในเฟรมนั้น
 */

import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Color3, Color4, Vector3 } from "@babylonjs/core/Maths/math";
import {
  DEFAULT_GENDER,
  type CharacterLook, type ClassLook,
  type EquipLook,
  type Gender,
} from "@wickhaven/shared";
import { createChibi, setGear, setHeldVisible, type ChibiModel } from "./models.js";

/** ขนาดที่เรนเดอร์จริง เผื่อให้คมตอนย่อลงบนการ์ด */
const SIZE = 192;
/** รอ shader พร้อมนานสุดกี่เฟรมก่อนยอมวาดเท่าที่มี */
const MAX_WAIT_FRAMES = 60;
/** มุมหันเริ่มต้น เอียงนิดหน่อยให้เห็นทั้งด้านหน้าและด้านข้าง */
export const DEFAULT_ROT = 0.5;

export interface ThumbMaker {
  /**
   * วาดตัวละครอาชีพนี้ลงบน canvas 2D ที่ให้มา
   *
   * `rotY` คือมุมหันรอบแกนตั้งเป็นเรเดียน ใช้ตอนลากหมุนดูอุปกรณ์ในหน้าต่างตัวละคร
   * สั่งซ้ำบน canvas ใบเดิมได้ถี่เท่าไรก็ได้ งานที่ยังไม่ได้วาดของใบนั้นจะถูกแทนที่
   */
  draw(
    target: HTMLCanvasElement,
    look: ClassLook,
    rotY?: number,
    gender?: Gender,
    gear?: EquipLook[],
    /** หน้าตาที่เลือกเอง (shared/looks.ts) */
    custom?: CharacterLook,
  ): void;
  dispose(): void;
}

/** ลายเซ็นของหน้าตา ใช้ดูว่าต้องสร้างโมเดลใหม่ไหม หรือแค่หมุนตัวเดิม */
function lookKey(look: ClassLook, gender: Gender, gear: EquipLook[], custom?: CharacterLook): string {
  return `${look.hair}|${look.outfit}|${look.accent}|${look.weapon}|${gender}`
    + `|${custom ? `${custom.skin}${custom.hair}${custom.hairStyle}${custom.eyes}` : ""}`
    + `|${gear.map((g) => `${g.shape}:${g.color}`).join(",")}`;
}

export function createThumbMaker(): ThumbMaker {
  const off = document.createElement("canvas");
  off.width = SIZE;
  off.height = SIZE;

  const engine = new Engine(off, true, { preserveDrawingBuffer: true }, false);
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0, 0, 0, 0);

  // เห็นตั้งแต่หัวจรดเข่า ไม่ใช่แค่หน้าเหมือนรูปใน HUD
  const cam = new ArcRotateCamera("tcam", Math.PI / 2 + 0.45, 1.22, 4.4, new Vector3(0, 1.1, 0), scene);
  cam.fov = 0.8;

  const hemi = new HemisphericLight("themi", new Vector3(0.2, 1, -0.3), scene);
  hemi.intensity = 0.95;
  hemi.groundColor = new Color3(0.4, 0.42, 0.5);
  const key = new DirectionalLight("tkey", new Vector3(-0.5, -0.8, 1), scene);
  key.intensity = 0.7;

  let model: ChibiModel | undefined;
  /** หน้าตาของโมเดลที่ค้างอยู่ในฉากตอนนี้ ตรงกันก็ไม่ต้องสร้างใหม่ */
  let modelKey = "";

  const copy = (target: HTMLCanvasElement): void => {
    const ctx = target.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, target.width, target.height);
    ctx.drawImage(off, 0, 0, target.width, target.height);
  };

  /**
   * คิวงานวาด **ต้องทำทีละใบจนจบ**
   *
   * การวาดหนึ่งใบไม่จบในทันที ต้องรอ `scene.isReady()` ข้ามเฟรมก่อนคัดลอกภาพ
   * ถ้าปล่อยให้เรียกซ้อนกันได้ ใบที่สองจะ dispose โมเดลของใบแรกทิ้งก่อนที่ใบแรกจะคัดลอก
   * ผลคือ **ทุกการ์ดได้ภาพของตัวสุดท้ายเหมือนกันหมด** ซึ่งเคยหลุดไปแล้วในหน้าเลือกตัวละคร
   *
   * งานของ canvas ใบเดียวกันที่ยังไม่ได้วาด จะถูกแทนที่ด้วยงานใหม่เสมอ
   * ลากหมุนรูปเร็ว ๆ จึงไม่สะสมเป็นคิวยาวให้ภาพตามมือไม่ทัน
   */
  const queue: Array<{
    target: HTMLCanvasElement;
    look: ClassLook;
    rotY: number;
    gender: Gender;
    gear: EquipLook[];
    custom?: CharacterLook;
  }> = [];
  let busy = false;

  const pump = (): void => {
    const job = queue.shift();
    if (!job) {
      busy = false;
      return;
    }
    busy = true;

    // อาชีพเดิมก็ใช้โมเดลตัวเดิม แค่หมุน — ลากหมุนแล้วไม่ต้องสร้างใหม่ทุกเฟรม
    const key = lookKey(job.look, job.gender, job.gear, job.custom);
    if (!model || key !== modelKey) {
      // ไม่ลบวัสดุ มันถูกแคชใช้ร่วมกันทั้งฉาก การ์ดใบถัดไปยังต้องใช้
      model?.root.dispose(false, false);
      model = createChibi(scene, job.look, undefined, job.gender, job.custom);
      setGear(scene, model, job.gear);
      // รูปนิ่งควรเห็นอาวุธด้วย ไม่ใช่มือเปล่าเหมือนตอนยืนในฉาก
      setHeldVisible(model, true);
      modelKey = key;
    }
    model.root.rotation.y = job.rotY;

    scene.render();

    // shader ของ StandardMaterial โหลดแบบ dynamic เฟรมแรกจึงอาจยังว่าง
    // รอจนฉากพร้อมแล้วค่อยคัดลอก ไม่งั้นการ์ดจะโล่งแบบไม่มี error ให้เห็น
    let frames = 0;
    const wait = (): void => {
      if (scene.isReady() || frames++ > MAX_WAIT_FRAMES) {
        scene.render();
        copy(job.target);
        pump();
        return;
      }
      scene.render();
      requestAnimationFrame(wait);
    };
    wait();
  };

  return {
    draw(target, look, rotY = DEFAULT_ROT, gender = DEFAULT_GENDER, gear = [], custom) {
      const pending = queue.findIndex((j) => j.target === target);
      if (pending >= 0) queue.splice(pending, 1);
      queue.push({ target, look, rotY, gender, gear, ...(custom ? { custom } : {}) });
      if (!busy) pump();
    },

    dispose() {
      scene.dispose();
      engine.dispose();
    },
  };
}
